"use client";
import { AlertTriangle, BookOpenText, Minus, Music, Plus, Trash2, X } from "lucide-react";
import { useTranslations } from "next-intl";
import React, { useId, useState } from "react";
import type { UseFormRegisterReturn, UseFormReturn } from "react-hook-form";
import { TargetLanguageInput } from "@/components/transcriptions/LanguagesInput";
import { MediaInput } from "@/components/transcriptions/MediaPreview";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@/components/ui/field";
import * as RadixForm from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import Link from "@/lib/locale/Link";
import { timeMsToSrt } from "@/lib/subs/srt";
import { fileKey } from "@/lib/upload/media-metadata";
import {
  type FileRejectionSummary,
  reasonTranslationKey,
  summarizeDropRejections,
} from "@/lib/upload/rejection-utils";
import { cn } from "@/lib/utils";
import { Uploader } from "./multipart-upload";
import type { SpeakerField } from "./speaker-fields";
import {
  BeginTranscriptionButton,
  SelectedMediaPreviewPanel,
  UrlInfoPreviewPanel,
} from "./UploadMediaPanels";
import { MAX_DURATION_S, MAX_FILES, MAX_SIZE_MB, type UploadFormData } from "./upload-form-schema";

// keepMinutes so a 30s file renders as "00:30" instead of ":30" (the default
// stripZeroes pass collapses the leading "00:" and a leading "0" too).
const formatDuration = (durationS: number) =>
  durationS > 0 ? timeMsToSrt({ timeMs: durationS * 1000, keepMinutes: true }) : "—";

const MAX_SPEAKER_COUNT = 10;
// Sensible starting point when the user enables diarization for a file —
// most calls have at least two speakers, and decrementing to 1 is one click.
const DEFAULT_SPEAKER_COUNT_ON = 2;

const SpeakerCountStepper = ({
  value,
  onChange,
  disabled,
}: {
  value: number;
  onChange: (next: number) => void;
  disabled?: boolean;
}) => {
  const t = useTranslations();

  if (value <= 0) {
    return (
      <label className="inline-flex items-center gap-2 text-xs text-foreground/80 cursor-pointer">
        <Switch
          checked={false}
          disabled={disabled}
          onCheckedChange={() => onChange(DEFAULT_SPEAKER_COUNT_ON)}
          aria-label={t("uploadForm.speakerCountToggle")}
        />
        {t("uploadForm.speakerCountToggle")}
      </label>
    );
  }

  return (
    <div className="inline-flex items-center gap-1 text-xs">
      <span className="text-foreground/80 mr-1">{t("uploadForm.speakerCountLabel")}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-6 w-6"
        disabled={disabled}
        onClick={() => onChange(value - 1)}
        aria-label={t("uploadForm.decreaseSpeakerCountLabel")}
      >
        <Minus size={12} />
      </Button>
      <span className="min-w-4 text-center tabular-nums">{value}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-6 w-6"
        disabled={disabled || value >= MAX_SPEAKER_COUNT}
        onClick={() => onChange(Math.min(MAX_SPEAKER_COUNT, value + 1))}
        aria-label={t("uploadForm.increaseSpeakerCountLabel")}
      >
        <Plus size={12} />
      </Button>
    </div>
  );
};

export const DropRejections = ({
  rejections,
  onDismiss,
  maxMb,
  max,
  maxHours,
}: {
  rejections: FileRejectionSummary[];
  onDismiss: () => void;
  /** Limit values used for interp in the rejection reason messages. */
  maxMb: number;
  max: number;
  maxHours: number;
}) => {
  const t = useTranslations();
  return (
    <div
      className="mt-4 rounded-2xl border border-destructive/50 bg-destructive/5 px-4 py-3"
      data-testid="dropzone-rejections"
      role="alert"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-destructive">
          <AlertTriangle size={16} />
          {t("uploadForm.rejectionsHeader", { count: rejections.length })}
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onDismiss}>
          {t("uploadForm.rejectionsDismiss")}
        </Button>
      </div>
      <ul className="space-y-1 text-sm text-destructive">
        {rejections.map((r, i) => (
          <li key={`${r.fileName}-${i}`}>
            <span className="font-medium">{r.fileName}</span>
            <span className="text-destructive/80">
              {" — "}
              {r.reasons
                .map((reason) => t(reasonTranslationKey(reason), { maxMb, max, maxHours }))
                .join(", ")}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const MediaFieldPreview = ({
  form,
  multiple,
  url,
  mediaValue,
  duration,
  sizeMb,
  type,
  mediaPreviewRef,
  resetFormFile,
  canSubmit,
  isLoading,
  uploader,
  uploadStatus,
  fileDurations,
  maxDurationS,
  speakerCounts,
  onSpeakerCountChange,
  speakerStepperDisabled,
}: {
  form: UseFormReturn<UploadFormData>;
  multiple: boolean;
  url: string | undefined;
  mediaValue: File | null;
  duration: number;
  sizeMb: number;
  type: string;
  mediaPreviewRef: React.RefObject<HTMLVideoElement | HTMLAudioElement | null>;
  resetFormFile: () => void;
  canSubmit: boolean;
  isLoading: boolean;
  uploader: React.RefObject<Uploader | null>;
  uploadStatus: "INITIAL" | "LOADING" | "ERROR" | "SUCCESS";
  fileDurations: Map<string, number>;
  maxDurationS: number;
  /** Per-file num_speakers map, only consulted in the multi-file list. */
  speakerCounts?: Map<string, number>;
  onSpeakerCountChange?: (fileKey: string, count: number) => void;
  speakerStepperDisabled?: boolean;
}) => {
  const t = useTranslations();
  const allFiles = (form.watch("mediaInput.files") ?? []) as File[];

  const removeAt = (index: number) => {
    const next = allFiles.filter((_, i) => i !== index);
    form.setValue("mediaInput.files", next, { shouldDirty: true, shouldValidate: true });
  };

  if (multiple && allFiles.length > 1) {
    const totalMb = allFiles.reduce((acc, f) => acc + f.size / 1024 / 1024, 0);
    const totalS = allFiles.reduce((acc, f) => acc + (fileDurations.get(fileKey(f)) ?? 0), 0);
    return (
      <div className="shadow-sm rounded-2xl px-4 py-4 bg-linear-to-r from-info-surface-from/20 to-info-surface-to/20 mt-4 sm:px-8">
        <div className="flex flex-col gap-2 mb-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-foreground/80 text-sm">
            {t("uploadForm.queuedFilesSummary", {
              count: allFiles.length,
              total: totalMb.toFixed(0),
              duration: formatDuration(totalS),
            })}
          </div>
          <Button
            title={t("uploadForm.queueClearAllLabel")}
            variant="ghost"
            size="sm"
            onClick={resetFormFile}
            type="button"
            className="self-start sm:self-auto"
          >
            <Trash2 size={14} className="mr-1" />
            {t("uploadForm.queueClearAllLabel")}
          </Button>
        </div>
        <ul className="divide-y divide-foreground/10 mb-4">
          {allFiles.map((f, i) => {
            const fileDurationS = fileDurations.get(fileKey(f)) ?? 0;
            const isTooLong = fileDurationS > 0 && fileDurationS > maxDurationS;
            return (
              <li
                key={fileKey(f)}
                className="py-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="min-w-0 break-all font-medium text-sm sm:truncate">
                      {f.name}
                    </div>
                    {isTooLong && (
                      <span className="text-[10px] uppercase tracking-wide font-medium rounded bg-destructive/15 text-destructive px-1.5 py-0.5">
                        {t("uploadForm.tooLongTag")}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-foreground/60">
                    <span>{formatDuration(fileDurationS)}</span>
                    &nbsp;·&nbsp;
                    <span>{(f.size / 1024 / 1024).toFixed(0)}MB</span>
                  </div>
                </div>
                {onSpeakerCountChange && (
                  <SpeakerCountStepper
                    value={speakerCounts?.get(fileKey(f)) ?? 0}
                    onChange={(next) => onSpeakerCountChange(fileKey(f), next)}
                    disabled={speakerStepperDisabled}
                  />
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeAt(i)}
                  aria-label={t("uploadForm.queueRemoveOneLabel")}
                  className="self-start sm:self-auto"
                >
                  <Trash2 size={16} />
                </Button>
              </li>
            );
          })}
        </ul>
        <div className="flex items-center justify-center">
          <BeginTranscriptionButton
            label={t("uploadForm.beginTranscriptionButton")}
            canSubmit={canSubmit}
            isLoading={isLoading}
          />
        </div>
      </div>
    );
  }

  return (
    <SelectedMediaPreviewPanel
      url={url}
      mediaName={mediaValue?.name}
      duration={duration}
      sizeMb={sizeMb}
      type={type}
      mediaPreviewRef={mediaPreviewRef}
      resetFormFile={resetFormFile}
      canSubmit={canSubmit}
      isLoading={isLoading}
      uploader={uploader}
      uploadStatus={uploadStatus}
      submitLabel={t("uploadForm.beginTranscriptionButton")}
      removeLabel="Remove file"
    />
  );
};

export const MediaFormField = ({
  form,
  url,
  mediaValue,
  duration,
  sizeMb,
  resetFormFile,
  type,
  mediaPreviewRef,
  canSubmit,
  uploader,
  uploadStatus,
  isLoading,
  urlInfoLoading,
  urlInfo,
  multiple = false,
  disabled = false,
  queueSlot,
  fileDurations,
  maxDurationS,
  computedRejections,
  speakerCounts,
  onSpeakerCountChange,
}: {
  form: UseFormReturn<UploadFormData>;
  url: string | undefined;
  mediaValue: File | null;
  duration: number;
  sizeMb: number;
  resetFormFile: () => void;
  type: string;
  mediaPreviewRef: React.RefObject<HTMLVideoElement | HTMLAudioElement | null>;
  canSubmit: boolean;
  uploader: React.RefObject<Uploader | null>;
  uploadStatus: "INITIAL" | "LOADING" | "ERROR" | "SUCCESS";
  isLoading: boolean;
  urlInfoLoading: boolean;
  urlInfo?: UrlInfo;
  multiple?: boolean;
  disabled?: boolean;
  /** Slot rendered immediately under the dropzone — used for the inline batch queue. */
  queueSlot?: React.ReactNode;
  fileDurations: Map<string, number>;
  maxDurationS: number;
  /**
   * Rejections derived from already-accepted files (e.g. too-long after
   * metadata read). Rendered in the same banner as dropzone-level
   * rejections. They reappear after dismiss if the underlying condition
   * still holds — remove the file to clear them.
   */
  computedRejections?: FileRejectionSummary[];
  speakerCounts?: Map<string, number>;
  onSpeakerCountChange?: (fileKey: string, count: number) => void;
}) => {
  const t = useTranslations();
  const [rejections, setRejections] = useState<FileRejectionSummary[]>([]);
  const combinedRejections = [...rejections, ...(computedRejections ?? [])];

  return (
    // One visual group for the media inputs: tight internal rhythm (space-y-4)
    // against the form's larger section gaps (space-y-8) — hierarchy, not a
    // flat stack of equal 32px gaps.
    <div className="space-y-4">
      <RadixForm.FormField
        control={form.control}
        name="mediaInput.files"
        render={({ field }) => {
          return (
            <RadixForm.FormItem>
              <RadixForm.FormLabel className="hidden">
                {t("uploadForm.mediaInputLabel")}
              </RadixForm.FormLabel>
              <RadixForm.FormControl>
                <MediaInput
                  dragOverText={t("uploadForm.dropZoneText")}
                  multiple={multiple}
                  maxFiles={multiple ? MAX_FILES : 1}
                  maxSize={MAX_SIZE_MB * 1024 * 1024}
                  disabled={disabled}
                  onReject={(rejected) =>
                    setRejections((prev) => [...prev, ...summarizeDropRejections(rejected)])
                  }
                  onChange={(files) => {
                    if (!multiple) {
                      field.onChange({ target: { value: files, name: field.name } });
                      return;
                    }
                    // Multi-file mode: append on subsequent drops, dedupe by
                    // name+size+lastModified, cap at MAX_FILES.
                    const current = (form.getValues("mediaInput.files") ?? []) as File[];
                    const seen = new Set(
                      current.map((f) => `${f.name}-${f.size}-${f.lastModified}`)
                    );
                    const incoming = files.filter(
                      (f) => !seen.has(`${f.name}-${f.size}-${f.lastModified}`)
                    );
                    const combined = [...current, ...incoming];
                    if (combined.length > MAX_FILES) {
                      // Cumulative overflow — surface as a rejection so the
                      // user sees which files didn't make it in.
                      const overflow = combined.slice(MAX_FILES).map((f) => ({
                        fileName: f.name,
                        reasons: ["too-many" as const],
                      }));
                      setRejections((prev) => [...prev, ...overflow]);
                    }
                    field.onChange({
                      target: { value: combined.slice(0, MAX_FILES), name: field.name },
                    });
                  }}
                />
              </RadixForm.FormControl>
              {combinedRejections.length > 0 && (
                <DropRejections
                  rejections={combinedRejections}
                  onDismiss={() => setRejections([])}
                  maxMb={MAX_SIZE_MB}
                  max={MAX_FILES}
                  maxHours={Math.floor(maxDurationS / 3600)}
                />
              )}
              <MediaFieldPreview
                form={form}
                multiple={multiple}
                url={url}
                mediaValue={mediaValue}
                duration={duration}
                sizeMb={sizeMb}
                type={type}
                mediaPreviewRef={mediaPreviewRef}
                resetFormFile={resetFormFile}
                canSubmit={canSubmit}
                isLoading={isLoading}
                uploader={uploader}
                uploadStatus={uploadStatus}
                fileDurations={fileDurations}
                maxDurationS={maxDurationS}
                speakerCounts={speakerCounts}
                onSpeakerCountChange={onSpeakerCountChange}
                speakerStepperDisabled={disabled}
              />
              {queueSlot}
              {disabled && (
                <p className="mt-2 text-sm text-foreground/70 italic">
                  {t("uploadForm.batchRunningHint")}
                </p>
              )}
              <RadixForm.FormMessage />
            </RadixForm.FormItem>
          );
        }}
      />

      <div className="flex items-center gap-4" role="separator">
        <span className="h-px flex-1 bg-border" />
        <span className="text-muted-foreground text-xs uppercase tracking-wider">
          {t("general.or")}
        </span>
        <span className="h-px flex-1 bg-border" />
      </div>
      <RadixForm.FormField
        control={form.control}
        name="mediaInput.youtubeUrl"
        render={({ field }) => (
          <RadixForm.FormItem className="text-center">
            <RadixForm.FormLabel className="font-medium text-base">
              {t("uploadForm.youtubeUrlInputLabel")}
            </RadixForm.FormLabel>
            <RadixForm.FormControl>
              <div className="relative">
                <Input
                  className="text-center text-base pr-12 sm:text-lg"
                  type="text"
                  placeholder={"https://www.domain.com/file..."}
                  disabled={disabled}
                  {...field}
                />
                <button
                  className="absolute right-0 top-1/2 -translate-y-1/2 -translate-x-1/2"
                  type="button"
                  aria-label="Clear URL"
                  onClick={() => {
                    if (disabled) return;
                    field.onChange({
                      target: { value: "", name: field.name },
                    });
                  }}
                >
                  <X className="text-foreground/80" />
                </button>
              </div>
            </RadixForm.FormControl>
            <RadixForm.FormDescription>
              {t("uploadForm.youtubeInputDescription")}
            </RadixForm.FormDescription>
            <RadixForm.FormMessage />
            <UrlInfoPreviewPanel
              urlInfoLoading={urlInfoLoading}
              urlInfo={urlInfo}
              canSubmit={canSubmit}
              isLoading={isLoading}
              submitLabel={t("uploadForm.beginTranscriptionButton")}
            />
          </RadixForm.FormItem>
        )}
      />
    </div>
  );
};

export const IsSongFormField = ({ form }: { form: UseFormReturn<UploadFormData> }) => {
  const t = useTranslations();

  return (
    <RadixForm.FormField
      control={form.control}
      name="isSong"
      render={({ field }) => (
        <RadixForm.FormItem className="space-y-3">
          <div className="flex items-center space-x-2">
            <RadixForm.FormControl>
              <Switch
                checked={field.value}
                onCheckedChange={(event) => {
                  if (event === true) {
                    form.setValue("transcriptionType", "MODAL_ALIGN");
                  } else {
                    form.setValue("transcriptionType", "MODAL_ASR_AND_ALIGN");
                  }
                  field.onChange(event);
                }}
              />
            </RadixForm.FormControl>
            <RadixForm.FormLabel>
              <span className="flex items-center justify-between">
                {t("align.transcribeSongLabel")}
                &nbsp;
                <Music size="18" />
              </span>
            </RadixForm.FormLabel>
          </div>
          <RadixForm.FormMessage />
        </RadixForm.FormItem>
      )}
    />
  );
};

export const AlignTextFormField = ({
  form,
  isSong,
  labelKey = "align.alignTextLabelSong",
  descriptionKey = "align.alignTextDescriptionSong",
  placeholderKey = "align.alignInputPlaceholderSong",
  guideHref = "/articles/add-subtitles-to-songs",
}: {
  form: UseFormReturn<UploadFormData>;
  isSong: boolean;
  labelKey?: string;
  descriptionKey?: string;
  placeholderKey?: string;
  guideHref?: string;
}) => {
  const t = useTranslations();

  return (
    <RadixForm.FormField
      control={form.control}
      name="text"
      render={({ field }) => (
        <RadixForm.FormItem
          className={cn({
            "flex flex-col space-y-2": true,
            hidden: !isSong,
          })}
        >
          <RadixForm.FormLabel className="block">{t(labelKey)}</RadixForm.FormLabel>
          <RadixForm.FormDescription className="block mb-2">
            {t(descriptionKey)}
            <Link
              href={guideHref}
              target="_blank"
              className="flex items-center space-x-1 underline"
            >
              <span>{t("general.guide")}</span>
              <BookOpenText size={16} />
            </Link>
          </RadixForm.FormDescription>
          <RadixForm.FormControl>
            <Textarea {...field} rows={16} placeholder={t(placeholderKey)} />
          </RadixForm.FormControl>
          <RadixForm.FormMessage />
        </RadixForm.FormItem>
      )}
    />
  );
};

export const TranscriptionTypeFormField = ({
  form,
  availableTranscriptionTypes,
}: {
  form: UseFormReturn<UploadFormData>;
  availableTranscriptionTypes: {
    key: string;
    description: string;
    label: string;
  }[];
}) => {
  const t = useTranslations();

  return (
    <RadixForm.FormField
      control={form.control}
      name="transcriptionType"
      render={({ field }) => (
        <RadixForm.FormItem>
          <FieldSet>
            <FieldLegend>{t("uploadForm.transcriptionTypeLabel")}</FieldLegend>
            <RadixForm.FormControl>
              <RadioGroup
                onValueChange={field.onChange}
                defaultValue={field.value}
                className="flex flex-col gap-2"
                data-slot="radio-group"
              >
                {availableTranscriptionTypes.map(({ key, description, label }) => (
                  <RadixForm.FormItem key={key} className="flex items-center space-x-3 space-y-0">
                    <RadixForm.FormControl>
                      <RadioGroupItem value={key} />
                    </RadixForm.FormControl>
                    <RadixForm.FormLabel className="font-normal">{label}</RadixForm.FormLabel>
                    <RadixForm.FormDescription>{description}</RadixForm.FormDescription>
                  </RadixForm.FormItem>
                ))}
              </RadioGroup>
            </RadixForm.FormControl>
            <RadixForm.FormMessage />
          </FieldSet>
        </RadixForm.FormItem>
      )}
    />
  );
};

export const TargetLanguageFormField = ({ form }: { form: UseFormReturn<UploadFormData> }) => {
  const t = useTranslations();
  const transcriptionType = form.watch("transcriptionType");
  const isSong = form.watch("isSong");

  return (
    <RadixForm.FormField
      control={form.control}
      name="targetLanguage"
      render={({ field }) => (
        <RadixForm.FormItem>
          <FieldSet className="gap-3">
            <FieldLegend>{t("uploadForm.selectLanguageLabel")}</FieldLegend>
            <FieldDescription>
              {transcriptionType === "ELEVENLABS_SCRIBE" || isSong
                ? t("uploadForm.selectLanguageDescriptionNoTranslation")
                : t("uploadForm.selectLanguageDescription")}
            </FieldDescription>
            <RadixForm.FormMessage />
            <TargetLanguageInput
              value={field.value ?? ""}
              setValue={field.onChange}
              includeAutomatic
            />
          </FieldSet>
        </RadixForm.FormItem>
      )}
    />
  );
};

export const SpeakersFormField = ({
  speakersFieldArray,
  registerName,
  nameError,
}: {
  speakersFieldArray: {
    fields: { id: string }[];
    append: (speaker: SpeakerField) => void;
    remove: (index: number) => void;
  };
  registerName: (index: number) => UseFormRegisterReturn;
  nameError: (index: number) => string | undefined;
}) => {
  const t = useTranslations();
  const id = useId();
  const nSpeakers = speakersFieldArray.fields.length;

  return (
    <FieldSet className="gap-3">
      <FieldLegend>{t("uploadForm.speakersLabel")}</FieldLegend>
      <FieldDescription>{t("uploadForm.speakersDescription")}</FieldDescription>
      {speakersFieldArray.fields.map((field, index) => {
        const inputId = `${id}-${field.id}`;
        const error = nameError(index);
        return (
          <Field key={field.id}>
            <FieldLabel htmlFor={inputId}>
              {t("uploadForm.speakerInputLabel", { index: index + 1 })}
            </FieldLabel>
            <div className="flex space-x-2">
              <Input
                id={inputId}
                type="text"
                placeholder={t("uploadForm.speakerInputLabel", { index: index + 1 })}
                aria-invalid={!!error}
                aria-describedby={error ? `${inputId}-error` : undefined}
                {...registerName(index)}
              />
              <Button
                type="button"
                variant="destructive"
                onClick={() => speakersFieldArray.remove(index)}
              >
                {t("uploadForm.removeSpeakerButton")}
              </Button>
            </div>
            {error && <FieldError id={`${inputId}-error`}>{error}</FieldError>}
          </Field>
        );
      })}
      <Button
        className="self-start"
        type="button"
        variant="secondary"
        onClick={() => speakersFieldArray.append({ name: `Speaker ${nSpeakers + 1}` })}
      >
        {t("uploadForm.addSpeakerButton")}
      </Button>
    </FieldSet>
  );
};
